import type { InfiniteData } from 'react-query';
import type {
  Paginated,
  AdminNotiQuery,
  NotificationItem,
  NotificationQuery,
  AdminNotificationItem,
  AdminNotificationsResponse,
} from 'src/@types/notification';

import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from 'react-query';

import { QUERY_KEY } from 'src/utils/queryKeys';

import {
  getNotifications,
  getMyNotifications,
  getNotificationById,
  markNotificationRead,
  fetchAdminNotifications,
  markNotificationsReadBulk,
  fetchAdminNotificationDetail,
} from 'src/features/services/notifications';

export function useMyNotifications(limit = 20) {
  const [searchParams] = useSearchParams();
  const q = (searchParams.get('q') || '').trim();

  return useInfiniteQuery(
    [QUERY_KEY.NOTIFICATION.MY_NOTIFICATIONS, limit, q],
    ({ pageParam }) =>
      getMyNotifications({
        limit,
        cursor: pageParam ?? null,
        q: q || undefined,
      }),
    {
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      staleTime: 30_000,
      cacheTime: 5 * 60_000,
      refetchOnWindowFocus: false,
    }
  );
}
export function useNotifications(params: NotificationQuery) {
  return useQuery<Paginated<NotificationItem>>(
    [QUERY_KEY.NOTIFICATION.INDEX, params],
    () => getNotifications(params),
    { keepPreviousData: true, staleTime: 30_000 }
  );
}

export function useNotificationByID(id?: string) {
  return useQuery<NotificationItem>(
    [QUERY_KEY.NOTIFICATION.NOTIFICATION_DETAIL, id],
    () => getNotificationById(id as string),
    { enabled: !!id, staleTime: 30_000 }
  );
}

type UseMarkReadOptions = {
  limit?: number;
  q?: string;
};

type PageShape = { items: NotificationItem[]; nextCursor: string | null };
export function patchIsReadInInfiniteData(
  data: InfiniteData<PageShape> | undefined,
  ids: string[],
  read: boolean
): InfiniteData<PageShape> | undefined {
  if (!data) return data;
  const setIds = new Set(ids);
  return {
    pageParams: data.pageParams,
    pages: data.pages.map((p) => ({
      ...p,
      items: p.items.map((it) => (setIds.has(it.id) ? { ...it, isRead: read } : it)),
    })),
  };
}

export function useMarkNotificationRead(opts?: UseMarkReadOptions) {
  const qc = useQueryClient();
  const key = [QUERY_KEY.NOTIFICATION.MY_NOTIFICATIONS, opts?.limit ?? 20, opts?.q ?? ''];

  return useMutation(
    async (params: { id: string; read?: boolean }) => {
      const { id, read = true } = params;
      return markNotificationRead(id, read);
    },
    {
      onMutate: async ({ id, read = true }) => {
        await qc.cancelQueries(key);
        const prev = qc.getQueryData<any>(key);

        qc.setQueryData(key, (old: any) => patchIsReadInInfiniteData(old, [id], read));

        return { prev };
      },
      onError: (_err, _vars, ctx) => {
        if (ctx?.prev) qc.setQueryData(key, ctx.prev);
      },
      onSettled: () => {
        qc.invalidateQueries(key);
      },
    }
  );
}

export function useMarkNotificationsReadBulk(opts?: UseMarkReadOptions) {
  const qc = useQueryClient();
  const key = [QUERY_KEY.NOTIFICATION.MY_NOTIFICATIONS, opts?.limit ?? 20, opts?.q ?? ''];

  return useMutation(
    async (params: { ids: string[]; read?: boolean }) => {
      const { ids, read = true } = params;
      return markNotificationsReadBulk(ids, read);
    },
    {
      onMutate: async ({ ids, read = true }) => {
        await qc.cancelQueries(key);
        const prev = qc.getQueryData<any>(key);

        qc.setQueryData(key, (old: any) => patchIsReadInInfiniteData(old, ids, read));

        return { prev };
      },
      onError: (_err, _vars, ctx) => {
        if (ctx?.prev) qc.setQueryData(key, ctx.prev);
      },
      onSettled: () => {
        qc.invalidateQueries(key);
      },
    }
  );
}

/**
 * Administrator
 */

export function useAdminNotifications(query?: AdminNotiQuery) {
  return useQuery<AdminNotificationsResponse>(
    [QUERY_KEY.NOTIFICATION.ADMIN, query],
    () => fetchAdminNotifications(query),
    {
      keepPreviousData: true,
      enabled: !!query,
    }
  );
}
export function useAdminNotificationDetail(id?: string) {
  return useQuery<AdminNotificationItem>(
    id ? [QUERY_KEY.NOTIFICATION.ADMIN_DETAIL, id] : [QUERY_KEY.NOTIFICATION.ADMIN_DETAIL_NULL],
    () => fetchAdminNotificationDetail(id as string),
    {
      enabled: !!id,
    }
  );
}
